import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { logEvent as logAnalyticsEvent } from '../analytics';
import { isValidUrl } from '../helpers';
import { getCorrelationId, logEvent, logException } from '../logging';
import generalConfig from '../resources/general.json';
import styles from './ScheduledRedirectNotice.module.css';
import getTypeSafeErrorMessage from '../utils/getTypeSafeErrorMessage';
import executeRedirect from '../utils/executeRedirect';
import { ScheduledRedirectAmplitudeEvent } from '../types';

const DEFAULT_DELAY_MS = 5000;

type ScheduledRedirectNoticeProps = {
    delayMs?: number;
    target?: string;
    callback?: () => void;
    amplitudeEvent?: ScheduledRedirectAmplitudeEvent;
};

export const ScheduledRedirectNotice: FC<ScheduledRedirectNoticeProps> = ({
    delayMs = DEFAULT_DELAY_MS,
    target = '',
    amplitudeEvent,
    callback,
}) => {
    const [remainingDelayMs, setRemainingDelayMs] = useState(delayMs);
    const hasValidTarget = !!target && isValidUrl(target);
    // Guard the redirect callback so it only fires once even if the effect
    // re-runs or both the immediate and timeout paths try to invoke it.
    const alertShownRef = useRef(false);
    // The countdown is stored in milliseconds for timing, then rounded up so
    // the UI shows the nearest remaining whole second.
    const countdownSeconds = Math.ceil(remainingDelayMs / 1000);
    const noticeTemplate =
        (generalConfig.providence as Record<string, string> | undefined)
            ?.scheduledRedirectNotice || '';
    const noticeMessage = noticeTemplate.replace(
        '{countdownSeconds}',
        countdownSeconds.toString()
    );

    const triggerRedirect = useCallback(() => {
        if (amplitudeEvent?.eventType) {
            logAnalyticsEvent(
                `${amplitudeEvent.eventType} - Executing`,
                amplitudeEvent.eventProperties
            );
        }

        void executeRedirect(target).catch((error: unknown) => {
            logException('Scheduled redirect failed', {
                exception: new Error(getTypeSafeErrorMessage(error)),
            });
        });
    }, [amplitudeEvent, target]);

    useEffect(() => {
        setRemainingDelayMs(delayMs);
        alertShownRef.current = false;
    }, [delayMs, target]);

    useEffect(() => {
        if (!hasValidTarget || !amplitudeEvent?.eventType) {
            return;
        }

        logAnalyticsEvent(
            amplitudeEvent.eventType,
            amplitudeEvent.eventProperties
        );
    }, [amplitudeEvent, hasValidTarget]);

    useEffect(() => {
        if (!hasValidTarget || remainingDelayMs > 0 || alertShownRef.current) {
            return;
        }

        alertShownRef.current = true;
        callback?.();
        triggerRedirect();
    }, [callback, hasValidTarget, remainingDelayMs, triggerRedirect]);

    useEffect(() => {
        if (!hasValidTarget) {
            logEvent({
                name: 'InvalidScheduledRedirectTarget',
                properties: {
                    correlationId: getCorrelationId(),
                    delayMs,
                    target,
                },
            });
            console.warn(
                'Invalid scheduled redirect target, banner will not render:',
                target
            );
            return;
        }

        if (delayMs <= 0) {
            setRemainingDelayMs(0);
            return;
        }

        // Tick once per second, keep the internal timer in milliseconds, and
        // round up to the nearest displayed second for the countdown message.
        const countdownId = window.setInterval(() => {
            setRemainingDelayMs((currentDelay) => {
                const nextDelay = Math.max(currentDelay - 1000, 0);

                if (nextDelay <= 0) {
                    window.clearInterval(countdownId);
                }

                return nextDelay;
            });
        }, 1000);

        return () => {
            window.clearInterval(countdownId);
        };
    }, [delayMs, hasValidTarget]);

    if (!hasValidTarget) {
        return null;
    }

    return (
        <p className={styles.notification}>
            <strong>{noticeMessage}</strong>
        </p>
    );
};
